import React, { useState, useEffect, useRef } from "react";
import { ReturContext } from "./ReturContext";
import TaskTable from "./TaskTable";
import TaskEdit from "./TaskEdit";
import RestituitCerereTab from "./tabs/RestituitCerereTab";
import OnlinePriceReductionTab from "./components/online/OnlinePriceReductionTab";
import { Tabs, Tab, Box, Typography, Button } from "@mui/material";
import {
  green,
  blue,
  grey,
  orange,
  red,
  purple,
  teal,
} from "@mui/material/colors";

const HIDDEN_TASK_RECOVERY_MS = 60000;

const TAB_STATUS_KEYS = {
  created: "2_CREAT",
  prospect: "PROSPECT",
  acceptat: "ACCEPTAT",
  tranzit: "TRANZIT",
  autentificare: "AUTENTIFICARE",
  online: "ONLINE",
};

const normalizeStatusKey = (value = "") =>
  value
    .toString()
    .trim()
    .toUpperCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, "_");

const STATUS_ALIASES = {
  CREATED: "2_CREAT",
  CREAT: "2_CREAT",
  "2_CREAT": "2_CREAT",
  PROSPECT: "PROSPECT",
  ACCEPTAT: "ACCEPTAT",
  TRANZIT: "TRANZIT",
  AUTENTIFICARE: "AUTENTIFICARE",
  ONLINE: "ONLINE",
};

const getStatusKeyFromTransition = (status) => {
  const normalizedStatus = normalizeStatusKey(status);
  return STATUS_ALIASES[normalizedStatus] || normalizedStatus;
};

const App = () => {
  const [returData, setReturData] = useState({
    returEmail: "",
    returNume: "",
    returTelefon: "",
  });
  const [selectedTaskId, setSelectedTaskId] = useState(null);
  const [selectedTab, setSelectedTab] = useState(0);
  const [onlineSubTab, setOnlineSubTab] = useState("expiring");
  const [reload, setReload] = useState(false);
  const [hiddenTaskIdsByStatus, setHiddenTaskIdsByStatus] = useState({});
  const hiddenTaskCleanupTimersRef = useRef({});
  const [draftCount, setDraftCount] = useState(0);

  useEffect(() => {
    const timers = hiddenTaskCleanupTimersRef.current;
    return () => {
      Object.values(timers).forEach((timerId) => window.clearTimeout(timerId));
    };
  }, []);

  useEffect(() => {
    const fetchDraftCount = async () => {
      try {
        const response = await fetch(
          "https://server-9p4hx.kinsta.app/api/stocmanager/cerere-restituit/restituiri/count-draft"
        );
        const data = await response.json();
        setDraftCount(data.total || 0);
      } catch (error) {
        console.error("Eroare la fetch draft count:", error);
      }
    };
    fetchDraftCount();
  }, []);

  const handleTaskSelect = (issueId) => {
    setSelectedTaskId(issueId);
  };

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
    setSelectedTaskId(null); // Reset the selected task when changing tabs
  };

  const handleOnlineSubTabChange = (_event, newValue) => {
    setSelectedTaskId(null);
    setOnlineSubTab(newValue);
  };

  const handleCloseTaskEdit = () => {
    setSelectedTaskId(null);
    setReload((currentReload) => !currentReload); // Toggle reload state to refresh tasks
  };
  const handleHideTaskEdit = () => {
    setSelectedTaskId(null);
  };

  const removeHiddenTaskFromStatus = (statusKey, issueId) => {
    if (!statusKey || !issueId) return;
    setHiddenTaskIdsByStatus((prevState) => {
      const existingIds = prevState[statusKey];
      if (!existingIds?.includes(issueId)) {
        return prevState;
      }
      const nextIds = existingIds.filter((id) => id !== issueId);
      const nextState = { ...prevState };
      if (nextIds.length > 0) {
        nextState[statusKey] = nextIds;
      } else {
        delete nextState[statusKey];
      }
      return nextState;
    });
  };

  const hideTaskFromStatus = (statusKey, issueId) => {
    if (!statusKey || !issueId) return;
    setHiddenTaskIdsByStatus((prevState) => {
      const existingIds = prevState[statusKey] || [];
      if (existingIds.includes(issueId)) {
        return prevState;
      }
      return {
        ...prevState,
        [statusKey]: [...existingIds, issueId],
      };
    });

    const cleanupKey = `${statusKey}:${issueId}`;
    if (hiddenTaskCleanupTimersRef.current[cleanupKey]) {
      window.clearTimeout(hiddenTaskCleanupTimersRef.current[cleanupKey]);
    }
    hiddenTaskCleanupTimersRef.current[cleanupKey] = window.setTimeout(() => {
      removeHiddenTaskFromStatus(statusKey, issueId);
      delete hiddenTaskCleanupTimersRef.current[cleanupKey];
    }, HIDDEN_TASK_RECOVERY_MS);
  };

  const handleTaskTransition = ({ issueId, fromTab, toStatus }) => {
    const fromStatusKey = TAB_STATUS_KEYS[fromTab];
    const toStatusKey = getStatusKeyFromTransition(toStatus);

    if (!fromStatusKey || !issueId || fromStatusKey === toStatusKey) {
      return false;
    }

    hideTaskFromStatus(fromStatusKey, issueId);
    removeHiddenTaskFromStatus(toStatusKey, issueId);
    setReload((currentReload) => !currentReload);
    return false;
  };

  const handleRefusedTasks = async () => {
    const newTab = window.open("", "_blank");
    newTab.document.write("<h3>Loading refused tasks...</h3>");

    try {
      const threeDaysAgo = new Date();
      threeDaysAgo.setDate(threeDaysAgo.getDate() - 3);
      const formattedDate = threeDaysAgo.toISOString();

      console.log("[INFO] Fetching refused tasks since:", formattedDate);

      const response = await fetch(
        `https://server-9p4hx.kinsta.app/api/tasks/jira-tasks/refused?modifiedSince=${formattedDate}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error(
          `Failed to fetch: ${response.status} - ${response.statusText}`
        );
      }

      const data = await response.json();

      console.log("[INFO] Refused tasks data:", data);

      if (!data || data.length === 0) {
        newTab.document.body.innerHTML =
          "<h3>No refused tasks found in the last 3 days.</h3>";
        return;
      }

      const refusedTasksHtml = `
            <h2>Refuzate în ultimele 3 zile</h2>
            <table style="width: 100%; border-collapse: collapse; text-align: left;">
                <thead>
                    <tr>
                        <th style="border: 1px solid black; padding: 8px;">Issue ID</th>
                        <th style="border: 1px solid black; padding: 8px;">Summary</th>
                        <th style="border: 1px solid black; padding: 8px;">Last Updated</th>
                        <th style="border: 1px solid black; padding: 8px;">Custom Field 10193</th>
                    </tr>
                </thead>
                <tbody>
                    ${data
                      .map((task) => {
                        // Extract description (ADF format)
                        const description =
                          task.description?.content
                            ?.map((block) =>
                              block.content
                                ?.map((inline) => inline.text || "")
                                .join("<br />")
                            )
                            .join("<br />") || "No description available";

                        return `
                                <tr>
                                    <td style="border: 1px solid black; padding: 8px;">${task.issue_id}</td>
                                    <td style="border: 1px solid black; padding: 8px;">${task.summary}</td>
                                    <td style="border: 1px solid black; padding: 8px;">${new Date(task.updated).toLocaleDateString()}</td>
                                    <td style="border: 1px solid black; padding: 8px;">${task.customfield_10193 || "Not Available"}</td>
                                </tr>
                            `;
                      })
                      .join("")}
                </tbody>
            </table>
        `;

      newTab.document.body.innerHTML = refusedTasksHtml;
    } catch (error) {
      newTab.document.body.innerHTML = `<h3>Error loading refused tasks: ${error.message}</h3>`;
      console.error("[ERROR] Error fetching refused tasks:", error);
    }
  };

  return (
    <ReturContext.Provider value={{ ...returData, setReturData }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          width: "100%",
          maxWidth: "100%",
          minWidth: 0,
          paddingTop: "20px",
          overflowX: "clip",
        }}
      >
        {/* Header pentru butonul Refuzat */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            marginBottom: "10px",
            paddingRight: "20px",
          }}
        >
          <Button
            variant="contained"
            color="secondary"
            onClick={handleRefusedTasks}
            sx={{
              backgroundColor: red[500],
              "&:hover": {
                backgroundColor: red[700],
              },
            }}
          >
            Refuzat
          </Button>
        </Box>

        {/* Tab-uri */}
        <Tabs
          value={selectedTab}
          onChange={handleTabChange}
          centered
          TabIndicatorProps={{
            style: {
              backgroundColor: "transparent",
            },
          }}
          sx={{
            backgroundColor: "transparent",
            ".MuiTab-root": {
              minWidth: "15%", // Dimensiunea minimă redusă pentru a comprima taburile
              maxWidth: "20%", // Adăugăm o dimensiune maximă pentru flexibilitate
              position: "relative",
              "&:not(:last-of-type)::after": {
                content: '""',
                display: "block",
                width: "1px",
                height: "40px",
                backgroundColor: grey[300],
                position: "absolute",
                right: 0,
                top: "calc(50% - 20px)",
              },
            },
          }}
        >
          <Tab
            label={
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                }}
              >
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: "50%",
                    backgroundColor: green[500],
                    marginBottom: "8px",
                  }}
                />
                <Typography variant="caption">Creat</Typography>
              </Box>
            }
          />
          <Tab
            label={
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                }}
              >
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: "50%",
                    backgroundColor: blue[500],
                    marginBottom: "8px",
                  }}
                />
                <Typography variant="caption">Prospect</Typography>
              </Box>
            }
          />
          <Tab
            label={
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                }}
              >
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: "50%",
                    backgroundColor: orange[500],
                    marginBottom: "8px",
                  }}
                />
                <Typography variant="caption">Acceptat</Typography>
              </Box>
            }
          />
          <Tab
            label={
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                }}
              >
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: "50%",
                    backgroundColor: red[500],
                    marginBottom: "8px",
                  }}
                />
                <Typography variant="caption">Tranzit</Typography>
              </Box>
            }
          />
          <Tab
            label={
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                }}
              >
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: "50%",
                    backgroundColor: purple[500],
                    marginBottom: "8px",
                  }}
                />
                <Typography variant="caption">Autentificare</Typography>
              </Box>
            }
          />
          <Tab
            label={
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                }}
              >
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: "50%",
                    backgroundColor: teal[500],
                    marginBottom: "8px",
                  }}
                />
                <Typography variant="caption">Online</Typography>
              </Box>
            }
          />
          <Tab
            label={
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  position: "relative",
                }}
              >
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: "50%",
                    backgroundColor: red[500],
                    marginBottom: "8px",
                  }}
                />
                <Typography variant="caption">Restituiri</Typography>
                {draftCount > 0 && (
                  <Box
                    sx={{
                      position: "absolute",
                      top: 0,
                      right: 0,
                      backgroundColor: red[500],
                      color: "#fff",
                      borderRadius: "50%",
                      width: 25,
                      height: 25,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "18px",
                      fontWeight: "bold",
                    }}
                  >
                    {draftCount}
                  </Box>
                )}
              </Box>
            }
          />
        </Tabs>

        {selectedTab === 5 && (
          <Tabs
            value={onlineSubTab}
            onChange={handleOnlineSubTabChange}
            aria-label="Secțiuni Online"
            variant="scrollable"
            scrollButtons="auto"
            sx={{ mt: 2, borderBottom: 1, borderColor: "divider" }}
          >
            <Tab
              id="online-tab-expiring"
              aria-controls="online-panel-expiring"
              value="expiring"
              label="Expiră curând"
              sx={{ textTransform: "none", fontWeight: 600 }}
            />
            <Tab
              id="online-tab-price-reduction"
              aria-controls="online-panel-price-reduction"
              value="price-reduction"
              label="Diminuare Preț"
              sx={{ textTransform: "none", fontWeight: 600 }}
            />
          </Tabs>
        )}

        {/* Tabele pentru taskuri */}
        <Box
          sx={{
            display: "grid",
            width: "100%",
            maxWidth: "100%",
            minWidth: 0,
            marginTop: "20px",
            gap: 2,
            gridTemplateColumns: selectedTaskId
              ? {
                  xs: "minmax(0, 1fr)",
                  xl: "minmax(0, 1fr) minmax(420px, min(32vw, 540px))",
                }
              : "minmax(0, 1fr)",
            alignItems: "flex-start",
            overflowX: "clip",
          }}
        >
          <Box sx={{ minWidth: 0, maxWidth: "100%" }}>
            {selectedTab === 0 && (
              <TaskTable
                onTaskSelect={handleTaskSelect}
                projectKey="LEB"
                status="2_CREAT"
                reload={reload}
                selectedTaskId={selectedTaskId}
                hiddenTaskIds={hiddenTaskIdsByStatus["2_CREAT"] || []}
              />
            )}
            {selectedTab === 1 && (
              <TaskTable
                onTaskSelect={handleTaskSelect}
                projectKey="LEB"
                status="PROSPECT"
                reload={reload}
                selectedTaskId={selectedTaskId}
                hiddenTaskIds={hiddenTaskIdsByStatus.PROSPECT || []}
              />
            )}
            {selectedTab === 2 && (
              <TaskTable
                onTaskSelect={handleTaskSelect}
                projectKey="LEB"
                status="ACCEPTAT"
                reload={reload}
                selectedTaskId={selectedTaskId}
                hiddenTaskIds={hiddenTaskIdsByStatus.ACCEPTAT || []}
              />
            )}
            {selectedTab === 3 && (
              <TaskTable
                onTaskSelect={handleTaskSelect}
                projectKey="LEB"
                status="TRANZIT"
                reload={reload}
                selectedTaskId={selectedTaskId}
                hiddenTaskIds={hiddenTaskIdsByStatus.TRANZIT || []}
              />
            )}
            {selectedTab === 4 && (
              <TaskTable
                onTaskSelect={handleTaskSelect}
                projectKey="LEB"
                status="AUTENTIFICARE"
                reload={reload}
                selectedTaskId={selectedTaskId}
                hiddenTaskIds={hiddenTaskIdsByStatus.AUTENTIFICARE || []}
              />
            )}
            {selectedTab === 5 && onlineSubTab === "expiring" && (
              <Box role="tabpanel" id="online-panel-expiring" aria-labelledby="online-tab-expiring">
                <TaskTable
                  onTaskSelect={handleTaskSelect}
                  projectKey="LEB"
                  status="ONLINE"
                  reload={reload}
                  selectedTaskId={selectedTaskId}
                  hiddenTaskIds={hiddenTaskIdsByStatus.ONLINE || []}
                />
              </Box>
            )}
            {selectedTab === 5 && onlineSubTab === "price-reduction" && (
              <Box role="tabpanel" id="online-panel-price-reduction" aria-labelledby="online-tab-price-reduction">
                <OnlinePriceReductionTab />
              </Box>
            )}
            {selectedTab === 6 && <RestituitCerereTab />}
          </Box>
          {selectedTaskId && (
            <Box
              sx={{
                width: "100%",
                maxWidth: "100%",
                minWidth: 0,
                position: { xl: "sticky" },
                top: { xl: 88 },
                overflowX: "clip",
                pr: { xl: 0.5 },
              }}
            >
              <TaskEdit
                issueId={selectedTaskId}
                style={{
                  width: "100%",
                  maxWidth: "100%",
                  marginLeft: 0,
                  marginRight: 0,
                }}
                tab={
                  selectedTab === 0
                    ? "created"
                    : selectedTab === 1
                      ? "prospect"
                      : selectedTab === 2
                        ? "acceptat"
                        : selectedTab === 3
                          ? "tranzit"
                          : selectedTab === 4
                            ? "autentificare"
                            : selectedTab === 6
                              ? "restituiri"
                              : "online"
                }
                onClose={handleCloseTaskEdit}
                onPanelClose={handleHideTaskEdit}
                reloadTasks={() => setReload((currentReload) => !currentReload)}
                onTaskTransition={handleTaskTransition}
              />
            </Box>
          )}
        </Box>
      </Box>
    </ReturContext.Provider>
  );
};

export default App;
